import { useMemo } from 'react';
import { ReportTypesV2 } from 'src/utils/reportsUtils';

import {
  ReportConfigV2,
  useReportConfigsV2Internal,
} from './useReportConfigsV2Internal';

type UseReportConfigsV2 = {
  reportConfigs: ReportConfigV2[];
  isLoading: boolean;
  isFetching: boolean;
};

export const useReportConfigsV2 = ({
  reportTypes,
}: {
  /**
   * The report types to load. If not provided, all report types will be loaded.
   */
  reportTypes?: ReportTypesV2[];
}): UseReportConfigsV2 => {
  const {
    reportConfigs: reportConfigsV2Inventory,
    isLoading: reportV2InventoryLoading,
    isFetching: reportV2InventoryFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.InventoryV2>({
    reportType: ReportTypesV2.InventoryV2,
  });
  const {
    reportConfigs: reportConfigsV2Sale,
    isLoading: reportV2SaleLoading,
    isFetching: reportV2SaleFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.SaleV2>({
    reportType: ReportTypesV2.SaleV2,
  });
  const {
    reportConfigs: reportConfigsV2Commission,
    isLoading: reportV2CommissionLoading,
    isFetching: reportV2CommissionFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.Commission>({
    reportType: ReportTypesV2.Commission,
  });

  const {
    reportConfigs: reportConfigsV2Purchase,
    isLoading: reportV2PurchaseLoading,
    isFetching: reportV2PurchaseFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.Purchase>({
    reportType: ReportTypesV2.Purchase,
  });

  const {
    reportConfigs: reportConfigsV2SaleLineItem,
    isLoading: reportV2SaleLineItemLoading,
    isFetching: reportV2SaleLineItemFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.SaleLineItem>({
    reportType: ReportTypesV2.SaleLineItem,
  });

  const {
    reportConfigs: reportConfigsV2PurchaseLineItem,
    isLoading: reportV2PurchaseLineItemLoading,
    isFetching: reportV2PurchaseLineItemFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.PurchaseOrderLineItem>({
    reportType: ReportTypesV2.PurchaseOrderLineItem,
  });

  const {
    reportConfigs: reportConfigsV2SoldTicket,
    isLoading: reportV2SoldTicketLoading,
    isFetching: reportV2SoldTicketFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.SoldTicket>({
    reportType: ReportTypesV2.SoldTicket,
  });

  const {
    reportConfigs: reportConfigsV2CostOfGoodsSold,
    isLoading: reportV2CostOfGoodsSoldLoading,
    isFetching: reportV2CostOfGoodsSoldFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.CostOfGoodsSold>({
    reportType: ReportTypesV2.CostOfGoodsSold,
  });

  const {
    reportConfigs: reportConfigsV2SaleCostAdjustments,
    isLoading: reportV2SaleCostAdjustmentsLoading,
    isFetching: reportV2SaleCostAdjustmentsFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.SaleCostAdjustments>({
    reportType: ReportTypesV2.SaleCostAdjustments,
  });

  const {
    reportConfigs: reportConfigsV2TicketGroup,
    isLoading: reportV2TicketGroupLoading,
    isFetching: reportV2TicketGroupFetching,
  } = useReportConfigsV2Internal<ReportTypesV2.TicketGroup>({
    reportType: ReportTypesV2.TicketGroup,
  });

  const reportConfigs = useMemo(() => {
    const reportConfigs = [];
    if (!reportTypes || reportTypes.includes(ReportTypesV2.InventoryV2)) {
      reportConfigs.push(...reportConfigsV2Inventory);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SaleV2)) {
      reportConfigs.push(...reportConfigsV2Sale);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.Commission)) {
      reportConfigs.push(...reportConfigsV2Commission);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.Purchase)) {
      reportConfigs.push(...reportConfigsV2Purchase);
    }
    if (
      !reportTypes ||
      reportTypes.includes(ReportTypesV2.PurchaseOrderLineItem)
    ) {
      reportConfigs.push(...reportConfigsV2PurchaseLineItem);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SaleLineItem)) {
      reportConfigs.push(...reportConfigsV2SaleLineItem);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SoldTicket)) {
      reportConfigs.push(...reportConfigsV2SoldTicket);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.CostOfGoodsSold)) {
      reportConfigs.push(...reportConfigsV2CostOfGoodsSold);
    }
    if (
      !reportTypes ||
      reportTypes.includes(ReportTypesV2.SaleCostAdjustments)
    ) {
      reportConfigs.push(...reportConfigsV2SaleCostAdjustments);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.TicketGroup)) {
      reportConfigs.push(...reportConfigsV2TicketGroup);
    }
    return reportConfigs;
  }, [
    reportTypes,
    reportConfigsV2Inventory,
    reportConfigsV2Sale,
    reportConfigsV2Commission,
    reportConfigsV2Purchase,
    reportConfigsV2PurchaseLineItem,
    reportConfigsV2SaleLineItem,
    reportConfigsV2SoldTicket,
    reportConfigsV2CostOfGoodsSold,
    reportConfigsV2SaleCostAdjustments,
    reportConfigsV2TicketGroup,
  ]);

  const isLoading = useMemo(() => {
    const isLoadingList = [];
    if (!reportTypes || reportTypes.includes(ReportTypesV2.InventoryV2)) {
      isLoadingList.push(reportV2InventoryLoading);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SaleV2)) {
      isLoadingList.push(reportV2SaleLoading);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.Commission)) {
      isLoadingList.push(reportV2CommissionLoading);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.Purchase)) {
      isLoadingList.push(reportV2PurchaseLoading);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SaleLineItem)) {
      isLoadingList.push(reportV2SaleLineItemLoading);
    }
    if (
      !reportTypes ||
      reportTypes.includes(ReportTypesV2.PurchaseOrderLineItem)
    ) {
      isLoadingList.push(reportV2PurchaseLineItemLoading);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SoldTicket)) {
      isLoadingList.push(reportV2SoldTicketLoading);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.CostOfGoodsSold)) {
      isLoadingList.push(reportV2CostOfGoodsSoldLoading);
    }
    if (
      !reportTypes ||
      reportTypes.includes(ReportTypesV2.SaleCostAdjustments)
    ) {
      isLoadingList.push(reportV2SaleCostAdjustmentsLoading);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.TicketGroup)) {
      isLoadingList.push(reportV2TicketGroupLoading);
    }
    return isLoadingList.some((loading) => loading);
  }, [
    reportTypes,
    reportV2InventoryLoading,
    reportV2SaleLoading,
    reportV2CommissionLoading,
    reportV2PurchaseLoading,
    reportV2SaleLineItemLoading,
    reportV2PurchaseLineItemLoading,
    reportV2SoldTicketLoading,
    reportV2CostOfGoodsSoldLoading,
    reportV2SaleCostAdjustmentsLoading,
    reportV2TicketGroupLoading,
  ]);

  const isFetching = useMemo(() => {
    const isFetchingList = [];
    if (!reportTypes || reportTypes.includes(ReportTypesV2.InventoryV2)) {
      isFetchingList.push(reportV2InventoryFetching);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SaleV2)) {
      isFetchingList.push(reportV2SaleFetching);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.Commission)) {
      isFetchingList.push(reportV2CommissionFetching);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.Purchase)) {
      isFetchingList.push(reportV2PurchaseFetching);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SaleLineItem)) {
      isFetchingList.push(reportV2SaleLineItemFetching);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.SoldTicket)) {
      isFetchingList.push(reportV2SoldTicketFetching);
    }
    if (
      !reportTypes ||
      reportTypes.includes(ReportTypesV2.PurchaseOrderLineItem)
    ) {
      isFetchingList.push(reportV2PurchaseLineItemFetching);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.CostOfGoodsSold)) {
      isFetchingList.push(reportV2CostOfGoodsSoldFetching);
    }
    if (
      !reportTypes ||
      reportTypes.includes(ReportTypesV2.SaleCostAdjustments)
    ) {
      isFetchingList.push(reportV2SaleCostAdjustmentsFetching);
    }
    if (!reportTypes || reportTypes.includes(ReportTypesV2.TicketGroup)) {
      isFetchingList.push(reportV2TicketGroupFetching);
    }
    return isFetchingList.some((fetching) => fetching);
  }, [
    reportTypes,
    reportV2InventoryFetching,
    reportV2SaleFetching,
    reportV2CommissionFetching,
    reportV2PurchaseFetching,
    reportV2SaleLineItemFetching,
    reportV2SoldTicketFetching,
    reportV2PurchaseLineItemFetching,
    reportV2CostOfGoodsSoldFetching,
    reportV2SaleCostAdjustmentsFetching,
    reportV2TicketGroupFetching,
  ]);

  return {
    reportConfigs,
    isLoading,
    isFetching,
  };
};
